/**
 * Tiny helpers to fire Meta Pixel and Google Analytics (gtag) events safely.
 * - No-op on the server
 * - No-op if the respective tag hasn't loaded (i.e. user hasn't given marketing consent)
 */

type MetaEvent = "Lead" | "ViewContent" | "Schedule" | "Contact" | "Search" | "CompleteRegistration";

export function trackPixelEvent(
  event: MetaEvent,
  params?: Record<string, unknown>,
  /** Pass to dedupe with the matching server-side CAPI event. */
  eventId?: string,
) {
  if (typeof window === "undefined") return;
  const fbq = window.fbq as ((...args: unknown[]) => void) | undefined;
  if (!fbq) return;
  try {
    const opts = eventId ? { eventID: eventId } : undefined;
    if (params && opts) fbq("track", event, params, opts);
    else if (params) fbq("track", event, params);
    else if (opts) fbq("track", event, {}, opts);
    else fbq("track", event);
  } catch {
    // Swallow — analytics must never break the UI
  }
}

export function trackGtagEvent(event: string, params?: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  if (!window.gtag) return;
  try {
    window.gtag("event", event, params ?? {});
  } catch {
    // Swallow
  }
}

/**
 * Fires equivalent conversion events to both Meta Pixel and GA4 in one call.
 * `meta` and `ga` use each platform's recommended event name + params.
 * Pass `eventId` (Meta) and `ga.params.event_id` for browser/server dedup.
 */
export function trackConversion(opts: {
  meta: { event: MetaEvent; params?: Record<string, unknown>; eventId?: string };
  ga: { event: string; params?: Record<string, unknown> };
}) {
  trackPixelEvent(opts.meta.event, opts.meta.params, opts.meta.eventId);
  trackGtagEvent(opts.ga.event, opts.ga.params);
}
